import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {QuestionExecution, QuestionExecutionSummaryDTO} from '../../../data/models/execution/question-execution';
import {Cached} from '../../../data/models/cached';
import {Observable} from 'rxjs';
import {AnswerOptionExecution} from '../../../data/models/execution/answer-execution';
import {QUESTION_TYPES} from '../../../../constants/questionTypes';

type CachedQuestionExecution = Cached & { question: QuestionExecution };

@Injectable({
  providedIn: 'root'
})
export class QuestionExecutionService {

  private readonly EXPIRE = 10 * 60 * 60 * 1000;

  constructor(
    private http: HttpClient,
  ) {
  }

  private questionCache: {
    [executionId: number]: {
      [questionId: number]: CachedQuestionExecution,
    }
  } = {};

  invalidQuestionExecutionInCache(
    executionId: number,
    questionId: number,
  ): void {
    const question = this.getQuestionExecutionFromCache(executionId, questionId);
    if (question === undefined) {
      return;
    }
    question.expiredAt = 0;
  }

  getQuestionExecutionFromCache(
    executionId: number,
    questionId: number,
  ): CachedQuestionExecution | undefined {
    if (this.questionCache[executionId] === undefined ||
      this.questionCache[executionId][questionId] === undefined ||
      this.questionCache[executionId][questionId].expiredAt < Date.now()) {
      return undefined;
    }
    return this.questionCache[executionId][questionId];
  }

  getQuestionExecutionSummary(
      executionId: number,
      urlCode?: string,
    ): Promise<QuestionExecutionSummaryDTO> {
    return this.http.get<QuestionExecutionSummaryDTO>(`api/test-executions/${executionId}/summary?urlCode=` + urlCode)
    .toPromise()
    .then(response => this.mapQuestionExecutionSummary(executionId, response));
  }

  getImage(imageName: string): Observable<Blob> {
    return this.http.get(`api/test-executions/getImage/${imageName}`, { responseType: 'blob' });
  }

  updateQuestionOptionInCache(
    executionId: number,
    questionId: number,
    updatedOption: AnswerOptionExecution,
  ): void {
    const cached = this.getQuestionExecutionFromCache(executionId, questionId);
    if (!cached) {
      return;
    }

    const normalizedOption = this.normalizeAnswerOption(updatedOption);
    const question = {...cached.question};

    const optionIdsToUnselect = new Set<number>();
    if (normalizedOption.selected) {
      normalizedOption.blockedOptionIds.forEach(id => optionIdsToUnselect.add(id));
      if (question.type?.techId === QUESTION_TYPES.SINGLE_ANSWER) {
        question.options.forEach(option => {
          if (option.optionId !== normalizedOption.optionId) {
            optionIdsToUnselect.add(option.optionId);
          }
        });
      }
    }

    question.options = question.options.map(option => {
      if (option.optionId === normalizedOption.optionId) {
        return {
          ...option,
          ...normalizedOption,
        };
      }
      if (optionIdsToUnselect.has(option.optionId)) {
        return {
          ...option,
          selected: false,
        };
      }
      return option;
    });

    cached.question = question;
    cached.expiredAt = Date.now() + this.EXPIRE;
  }

  private mapQuestionExecutionSummary(
    executionId: number,
    summary: QuestionExecutionSummaryDTO,
  ): QuestionExecutionSummaryDTO {
    const mappedQuestions = summary.questions.map(question => this.mapQuestionExecution(question));
    this.saveQuestionsToCache(executionId, mappedQuestions);
    return {
      ...summary,
      questions: mappedQuestions,
      allQuestionsRequired: summary.allQuestionsRequired ?? true,
    };
  }

  private mapQuestionExecution(question: QuestionExecution): QuestionExecution {
    const mappedQuestion: any = {...question};
    if (mappedQuestion.minAnswers === undefined) {
      mappedQuestion.minAnswers = mappedQuestion.minSelectedOptions ?? null;
    }
    if (mappedQuestion.maxAnswers === undefined) {
      mappedQuestion.maxAnswers = mappedQuestion.maxSelectedOptions ?? null;
    }
    delete mappedQuestion.minSelectedOptions;
    delete mappedQuestion.maxSelectedOptions;
    mappedQuestion.options = (question.options || []).map(option => this.normalizeAnswerOption(option));
    return mappedQuestion as QuestionExecution;
  }

  private normalizeAnswerOption(option: AnswerOptionExecution): AnswerOptionExecution {
    return {
      ...option,
      selected: this.normalizeSelected(option.selected),
      blockedOptionIds: option.blockedOptionIds ? [...option.blockedOptionIds] : [],
      blockingOptionIds: option.blockingOptionIds ? [...option.blockingOptionIds] : [],
    };
  }

  private normalizeSelected(selected: AnswerOptionExecution['selected']): boolean {
    if (typeof selected === 'string') {
      return selected.toLowerCase() === 'true';
    }
    return !!selected;
  }

  private saveQuestionsToCache(executionId: number, questions: QuestionExecution[]): void {
    if (!this.questionCache[executionId]) {
      this.questionCache[executionId] = {};
    }
    const expiresAt = Date.now() + this.EXPIRE;
    questions.forEach(question => {
      this.questionCache[executionId][question.questionId] = {
        question,
        expiredAt: expiresAt,
      };
    });
  }
}
